/**
 * Range Parser Utility
 *
 * Parses reference range strings from ICP test data into structured formats.
 * Handles formats like "6.2 - 8.5 dKH" or "380 - 500 mg/l".
 */

export interface ParsedRange {
  min: number;
  max: number;
  unit: string;
}

/**
 * Parse a reference range string into min, max, and unit components.
 *
 * @param rangeStr - Range string like "6.2 - 8.5 dKH" or "380 - 500 mg/l"
 * @returns Parsed range object or null if parsing fails
 *
 * @example
 * parseRange("6.2 - 8.5 dKH") // { min: 6.2, max: 8.5, unit: "dKH" }
 * parseRange("380 - 500 mg/l") // { min: 380, max: 500, unit: "mg/l" }
 * parseRange("invalid") // null
 */
export function parseRange(rangeStr?: string): ParsedRange | null {
  if (!rangeStr) return null;

  // Match patterns like "6.2 - 8.5 dKH" or "380 - 500 mg/l"
  // Captures: (number) - (number) optional(unit)
  const match = rangeStr.match(/([\d.]+)\s*-\s*([\d.]+)\s*(.+)?/);

  if (!match) return null;

  const min = parseFloat(match[1] || '');
  const max = parseFloat(match[2] || '');
  const unit = match[3]?.trim() || '';

  // Validate that we got valid numbers
  if (isNaN(min) || isNaN(max)) return null;

  return { min, max, unit };
}

/**
 * Extract numeric value from a desired level string.
 *
 * @param levelStr - Level string like "7.5 dKH" or "420 ppm"
 * @returns Numeric value or null if parsing fails
 *
 * @example
 * parseDesiredLevel("7.5 dKH") // 7.5
 * parseDesiredLevel("420 ppm") // 420
 * parseDesiredLevel("invalid") // null
 */
export function parseDesiredLevel(levelStr?: string): number | null {
  if (!levelStr) return null;

  // Extract first numeric value from string
  const match = levelStr.match(/([\d.]+)/);

  if (!match) return null;

  const value = parseFloat(match[1]!);

  return isNaN(value) ? null : value;
}
